/**
 * SEO Landing Pages — content data for Portuguese diaspora dating pages.
 * Each entry targets a specific country or city where Portuguese communities thrive.
 */

const SEO_PAGES = {
  'portuguese-dating-france': {
    slug: 'portuguese-dating-france',
    country: 'France',
    flag: '\u{1F1EB}\u{1F1F7}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in France',
    metaDescription: 'Meet Portuguese singles living in France. LusoLove connects the 700,000+ Portuguese community across Paris, Lyon, Bordeaux and beyond. Find someone who understands saudade.',
    h1: 'Portuguese Dating in France',
    heroSubtitle: 'Connecting 700,000+ Portuguese hearts across l\'Hexagone',
    cities: ['Paris', 'Lyon', 'Bordeaux', 'Toulouse', 'Marseille', 'Nantes'],
    communitySize: '700,000+',
    content: [
      {
        heading: 'The largest Portuguese community in Europe',
        text: 'France is home to the largest Portuguese diaspora in Europe, with over 700,000 people of Portuguese heritage living across the country. From the bustling streets of Paris to the sun-drenched south in Toulouse and Marseille, Portuguese culture thrives in every corner of France. Yet finding a partner who shares your roots, your language, and your love of bacalhau on Christmas Eve can feel impossible on mainstream dating apps.',
      },
      {
        heading: 'Why Portuguese in France choose LusoLove',
        text: 'LusoLove was built specifically for the Portuguese diaspora. We understand that dating is about more than swiping \u2014 it\u2019s about finding someone who gets the feeling of saudade, who knows why your av\u00f3 is the most important person at every family gathering, and who\u2019ll argue with you about whether Benfica or Porto is the real champion. Our platform connects verified Portuguese singles across France, with real profiles and genuine intentions.',
      },
      {
        heading: 'Find love in your city',
        text: 'Whether you\u2019re in Paris working in finance, in Lyon studying engineering, or in Bordeaux building your career \u2014 LusoLove helps you find Portuguese singles nearby. Our distance-based matching shows you profiles within your area, and our city-level filters let you discover the Portuguese community in any French city. No more feeling like you\u2019re the only Portuguese person on the app.',
      },
    ],
    stats: { members: '1,200+', matches: '340+', cities: 8 },
  },

  'portuguese-dating-switzerland': {
    slug: 'portuguese-dating-switzerland',
    country: 'Switzerland',
    flag: '\u{1F1E8}\u{1F1ED}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Switzerland',
    metaDescription: 'Find Portuguese singles in Switzerland. LusoLove serves the 300,000+ Portuguese community in Zurich, Geneva, Lausanne, Bern and Basel. Real connections, real culture.',
    h1: 'Portuguese Dating in Switzerland',
    heroSubtitle: 'Where Swiss precision meets Portuguese passion',
    cities: ['Zurich', 'Geneva', 'Lausanne', 'Bern', 'Basel'],
    communitySize: '300,000+',
    content: [
      {
        heading: 'A thriving Portuguese community in the heart of Europe',
        text: 'Switzerland is home to over 300,000 Portuguese residents, making them one of the largest foreign communities in the country. From the financial hub of Zurich to the international city of Geneva, Portuguese people have built strong communities while maintaining deep ties to their homeland. But finding a partner who understands both your Swiss work ethic and your Portuguese heart? That\u2019s where LusoLove comes in.',
      },
      {
        heading: 'Dating that understands your dual identity',
        text: 'Living in Switzerland as a Portuguese person means navigating two worlds. You appreciate Swiss punctuality but miss the spontaneity of a Portuguese summer evening. You love your career opportunities but dream of the Algarve coast. LusoLove connects you with people who live this same reality \u2014 Portuguese singles in Switzerland who understand the beautiful complexity of diaspora life.',
      },
      {
        heading: 'Verified profiles, genuine connections',
        text: 'Every LusoLove profile is verified to ensure you\u2019re meeting real people with real intentions. Our matching system considers your location, your preferences, and your relationship goals. Whether you\u2019re looking for a serious relationship, friendship, or simply someone to share a pastel de nata with in your city \u2014 we\u2019ve got you covered across all major Swiss cities.',
      },
    ],
    stats: { members: '600+', matches: '180+', cities: 5 },
  },

  'portuguese-dating-luxembourg': {
    slug: 'portuguese-dating-luxembourg',
    country: 'Luxembourg',
    flag: '\u{1F1F1}\u{1F1FA}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Luxembourg',
    metaDescription: 'The Portuguese community makes up 15% of Luxembourg\u2019s population. LusoLove is the dating platform built for you. Find love among fellow Portuguese in the Grand Duchy.',
    h1: 'Portuguese Dating in Luxembourg',
    heroSubtitle: 'Where 15% of the population shares your roots',
    cities: ['Luxembourg City', 'Esch-sur-Alzette'],
    communitySize: '100,000+',
    content: [
      {
        heading: 'The most Portuguese country outside Portugal',
        text: 'Luxembourg has the highest concentration of Portuguese people relative to population anywhere in the world outside Portugal itself. With over 100,000 Portuguese residents making up roughly 15% of the total population, Portuguese culture is woven into the fabric of Luxembourgish society. From Portuguese restaurants on every corner to community festivals, the presence is unmistakable \u2014 yet finding a Portuguese partner who matches your values and lifestyle isn\u2019t always easy.',
      },
      {
        heading: 'Built for the Luxembourg Portuguese community',
        text: 'LusoLove understands the unique dynamics of being Portuguese in Luxembourg. Many people in the community work in banking, EU institutions, construction, and hospitality. Whether you arrived last year or your family has been here for generations, our platform connects you with verified Portuguese singles who share your cultural background and understand the importance of family, tradition, and a good cozido \u00e0 portuguesa.',
      },
      {
        heading: 'Small country, big community, real matches',
        text: 'In a country as small as Luxembourg, word travels fast and everyone seems to know everyone. LusoLove gives you the privacy to explore connections discreetly while ensuring every profile is genuine. Our proximity matching works perfectly for Luxembourg\u2019s compact geography, showing you Portuguese singles just minutes away.',
      },
    ],
    stats: { members: '300+', matches: '90+', cities: 2 },
  },

  'portuguese-dating-germany': {
    slug: 'portuguese-dating-germany',
    country: 'Germany',
    flag: '\u{1F1E9}\u{1F1EA}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Germany',
    metaDescription: 'Meet Portuguese singles in Germany. LusoLove connects the growing Portuguese community across Berlin, Munich, Hamburg, Frankfurt and more. Authentic connections, verified profiles.',
    h1: 'Portuguese Dating in Germany',
    heroSubtitle: 'Connecting Portuguese hearts across Deutschland',
    cities: ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne', 'Stuttgart'],
    communitySize: '200,000+',
    content: [
      {
        heading: 'A growing Portuguese community in Europe\u2019s powerhouse',
        text: 'Germany\u2019s booming economy has attracted over 200,000 Portuguese residents in recent years, making it one of the fastest-growing Portuguese communities in Europe. From Berlin\u2019s creative scene to Munich\u2019s tech industry, Portuguese professionals are making their mark across Germany. But when it comes to dating, the cultural gap on mainstream apps can make it hard to find someone who truly gets you.',
      },
      {
        heading: 'More than just language \u2014 it\u2019s about culture',
        text: 'LusoLove goes beyond matching you with someone who speaks Portuguese. We connect you with people who understand the importance of Sunday lunch with family, who know the difference between a festa and a party, and who\u2019ll happily drive four hours to attend a Portuguese community event. Our platform brings together Portuguese singles across all of Germany\u2019s major cities.',
      },
      {
        heading: 'Distance matching across Germany',
        text: 'Germany is a big country, and the Portuguese community is spread across many cities. LusoLove\u2019s distance-based matching helps you find Portuguese singles near you, whether you\u2019re in bustling Berlin or quiet Dortmund. Set your preferred radius and discover people within your reach \u2014 because the best connections happen when meeting up is actually possible.',
      },
    ],
    stats: { members: '500+', matches: '120+', cities: 8 },
  },

  'portuguese-dating-belgium': {
    slug: 'portuguese-dating-belgium',
    country: 'Belgium',
    flag: '\u{1F1E7}\u{1F1EA}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Belgium',
    metaDescription: 'Find Portuguese singles in Belgium. LusoLove connects the 50,000+ Portuguese community in Brussels, Antwerp, Ghent and Li\u00e8ge. Verified profiles, real connections.',
    h1: 'Portuguese Dating in Belgium',
    heroSubtitle: 'Finding Portuguese love in the heart of the EU',
    cities: ['Brussels', 'Antwerp', 'Ghent', 'Li\u00e8ge'],
    communitySize: '50,000+',
    content: [
      {
        heading: 'Portuguese roots in the capital of Europe',
        text: 'Belgium, and particularly Brussels, is home to a vibrant Portuguese community of over 50,000 people. Many work in EU institutions, international organizations, and the service sector. The Portuguese presence in Belgium dates back decades, creating a well-established community with its own churches, associations, and cultural events. Yet finding a Portuguese partner in Belgium\u2019s multilingual, multicultural landscape can be surprisingly challenging.',
      },
      {
        heading: 'Designed for diaspora dating',
        text: 'LusoLove understands that being Portuguese in Belgium means navigating French, Dutch, and English while keeping your heart firmly Portuguese. Our platform is designed specifically for the diaspora experience \u2014 connecting you with verified Portuguese singles who share your cultural values, your love of family, and your appreciation for the simpler things in life that make Portuguese culture so special.',
      },
      {
        heading: 'Join a growing community',
        text: 'Every week, new Portuguese singles in Belgium join LusoLove looking for meaningful connections. Whether you\u2019re in Brussels for work, Antwerp for studies, or have been in Belgium your whole life \u2014 there\u2019s someone here who\u2019s looking for exactly what you are. Create your profile in minutes and start discovering Portuguese singles near you.',
      },
    ],
    stats: { members: '200+', matches: '60+', cities: 4 },
  },

  'portuguese-dating-paris': {
    slug: 'portuguese-dating-paris',
    country: 'France',
    flag: '\u{1F1EB}\u{1F1F7}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Paris',
    metaDescription: 'Meet Portuguese singles in Paris. Over 200,000 Portuguese people call the Paris region home. LusoLove connects you with verified profiles from the Portuguese community in \u00cele-de-France.',
    h1: 'Portuguese Dating in Paris',
    heroSubtitle: 'Love finds a way in the City of Light',
    cities: ['Paris'],
    communitySize: '200,000+',
    content: [
      {
        heading: 'The Portuguese heart of Paris',
        text: 'The Paris region (\u00cele-de-France) is home to over 200,000 Portuguese people, making it the single largest concentration of Portuguese outside Lisbon. From the vibrant community in the 11th arrondissement to the families in the suburbs, Portuguese culture pulses through the city. LusoLove helps you find someone in this vast city who shares your roots and your dreams.',
      },
      {
        heading: 'Paris dating, Portuguese style',
        text: 'Parisian dating culture can feel cold and transactional. As a Portuguese person, you know that real connection takes warmth, honesty, and a bit of humor. LusoLove brings Portuguese dating values to Paris \u2014 where getting to know someone means sharing a bifana at a Portuguese caf\u00e9, not just exchanging messages. Our platform prioritizes genuine profiles and real intentions over superficial swiping.',
      },
      {
        heading: 'From the first message to the first meeting',
        text: 'LusoLove\u2019s messaging system and proximity matching make it easy to move from online to offline. See Portuguese singles near your arrondissement, start a conversation, and plan to meet at one of Paris\u2019s many Portuguese spots. Because the best relationships start with a real connection, not just a profile photo.',
      },
    ],
    stats: { members: '450+', matches: '130+', cities: 1 },
  },

  'portuguese-dating-geneva': {
    slug: 'portuguese-dating-geneva',
    country: 'Switzerland',
    flag: '\u{1F1E8}\u{1F1ED}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Geneva',
    metaDescription: 'Find Portuguese singles in Geneva. LusoLove connects the large Portuguese community in Gen\u00e8ve and Vaud region. Verified profiles, genuine intentions.',
    h1: 'Portuguese Dating in Geneva',
    heroSubtitle: 'Where lac L\u00e9man meets saudade',
    cities: ['Geneva', 'Lausanne'],
    communitySize: '80,000+',
    content: [
      {
        heading: 'A Portuguese stronghold in French-speaking Switzerland',
        text: 'The Geneva region and Canton de Vaud are home to one of Switzerland\u2019s largest Portuguese communities, with over 80,000 people. Portuguese is one of the most spoken languages in Geneva after French, English, and German. The community is deeply integrated yet proudly maintains its cultural identity through associations, restaurants, and annual festivals.',
      },
      {
        heading: 'International city, Portuguese soul',
        text: 'Geneva\u2019s international character means you\u2019re surrounded by people from every country \u2014 but finding someone who speaks your cultural language is different. LusoLove connects you with Portuguese singles in the Lake Geneva region who understand what it means to be Portuguese abroad. Whether you work at the UN, in hospitality, or in Geneva\u2019s thriving watch industry \u2014 your match could be closer than you think.',
      },
      {
        heading: 'Start your story today',
        text: 'Creating a LusoLove profile takes just 2 minutes. Add your photos, tell us about yourself, and start discovering Portuguese singles in Geneva and Lausanne. Our verified profiles and distance matching ensure every connection is real and reachable.',
      },
    ],
    stats: { members: '250+', matches: '70+', cities: 2 },
  },

  'portuguese-dating-zurich': {
    slug: 'portuguese-dating-zurich',
    country: 'Switzerland',
    flag: '\u{1F1E8}\u{1F1ED}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Zurich',
    metaDescription: 'Meet Portuguese singles in Zurich. LusoLove connects the Portuguese community in Z\u00fcrich and German-speaking Switzerland. Verified profiles, real connections.',
    h1: 'Portuguese Dating in Zurich',
    heroSubtitle: 'Finding amor in the financial capital',
    cities: ['Zurich', 'Basel', 'Bern'],
    communitySize: '60,000+',
    content: [
      {
        heading: 'Portuguese in German-speaking Switzerland',
        text: 'Zurich and the German-speaking cantons are home to a growing Portuguese community of over 60,000 people. Many came for career opportunities in banking, technology, and engineering. While the professional opportunities are excellent, the cultural adjustment can be significant \u2014 and dating in Switzerland\u2019s more reserved culture can feel particularly challenging for warm, sociable Portuguese people.',
      },
      {
        heading: 'Cultural compatibility matters',
        text: 'On mainstream Swiss dating apps, cultural differences can create misunderstandings from the first message. LusoLove eliminates that barrier by connecting you exclusively with Portuguese singles who share your background. We understand that for Portuguese people, dating isn\u2019t just about two individuals \u2014 it\u2019s about two families, two worlds coming together.',
      },
      {
        heading: 'Join the Portuguese community in Zurich',
        text: 'LusoLove is more than a dating app \u2014 it\u2019s a bridge between Portuguese people living in Switzerland. Our platform helps you find not just romantic connections but a sense of community and belonging in your adopted home. Sign up today and discover who\u2019s waiting for you.',
      },
    ],
    stats: { members: '200+', matches: '55+', cities: 3 },
  },

  'portuguese-dating-brussels': {
    slug: 'portuguese-dating-brussels',
    country: 'Belgium',
    flag: '\u{1F1E7}\u{1F1EA}',
    ptFlag: '\u{1F1F5}\u{1F1F9}',
    title: 'Portuguese Dating in Brussels',
    metaDescription: 'Find Portuguese singles in Brussels. LusoLove connects the Portuguese community in Belgium\u2019s capital. EU professionals, students, families \u2014 all verified, all genuine.',
    h1: 'Portuguese Dating in Brussels',
    heroSubtitle: 'Love in the capital of Europe',
    cities: ['Brussels'],
    communitySize: '30,000+',
    content: [
      {
        heading: 'The Portuguese community in Brussels',
        text: 'Brussels brings together Portuguese people from all walks of life \u2014 EU professionals, international students, hospitality workers, and second-generation Belgians with Portuguese roots. The city\u2019s Portuguese community, over 30,000 strong, is vibrant and active, with restaurants, cultural associations, and annual celebrations keeping the connection to Portugal alive.',
      },
      {
        heading: 'A platform that understands expat dating',
        text: 'Dating as a Portuguese person in Brussels means navigating multiple languages, cultures, and expectations. LusoLove cuts through the complexity by connecting you directly with Portuguese singles who share your cultural DNA. No more explaining why you want to go to Portugal for every holiday or why your mum calls you three times a day \u2014 your match already gets it.',
      },
      {
        heading: 'Create your profile now',
        text: 'Join hundreds of Portuguese singles already on LusoLove in Brussels. Our simple onboarding takes just 2 minutes, and our verification system ensures every profile is real. Start discovering authentic Portuguese connections in Brussels today.',
      },
    ],
    stats: { members: '150+', matches: '40+', cities: 1 },
  },
};

export default SEO_PAGES;
